import { useTranslation } from 'react-i18next'
import { Dispatch, FC, SetStateAction, useCallback, useEffect } from 'react'
import { IDeviationType } from '../../../generated-types/deviation-type'
import { useFormContext } from 'react-hook-form'
import { ReactComponent as Locality } from 'src/assets/svg/locality.svg'
import { ReactComponent as Mooring } from 'src/assets/icons/mooring.svg'
import { ReactComponent as Equipment } from 'src/assets/icons/equipments.svg'
import { ReactComponent as Component } from 'src/assets/icons/components.svg'
import { Flex } from 'src/styles/flexComponent'
import { Card } from 'src/styles/Index'
import { Typography } from '@mui/material'
import { IDeviationStepperFormStep } from 'src/generated-types/form-wizard'
import { styled } from '@mui/material'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'

const SelectTypeStep: FC<{
  setDisabled: Dispatch<SetStateAction<boolean>>
  setActiveStep: (step: IDeviationStepperFormStep) => void
  setIsStepperHidden: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ setDisabled, setActiveStep, setIsStepperHidden }) => {
  const { t: translate } = useTranslation(['common', 'deviation'])
  const { getValues, reset } = useFormContext()

  const deviationTypes = [
    {
      title: translate('to-locality', { ns: 'deviation' }),
      Icon: Locality,
      value: IDeviationType.Locality,
      onClick: e => handleChange(e, IDeviationType.Locality, IDeviationStepperFormStep.Step_5),
    },
    {
      title: translate('to-mooring', { ns: 'deviation' }),
      Icon: Mooring,
      value: IDeviationType.Mooring,
      onClick: e => handleChange(e, IDeviationType.Position, IDeviationStepperFormStep.Step_3),
    },
    {
      title: translate('to-additional-equipment', { ns: 'deviation' }),
      Icon: Equipment,
      value: IDeviationType.Equipment,
      onClick: e => handleChange(e, IDeviationType.Equipment, IDeviationStepperFormStep.Step_4),
    },
    {
      title: translate('to-net-or-ring', { ns: 'deviation' }),
      Icon: Component,
      value: IDeviationType.MainComponent,
      onClick: e => handleChange(e, IDeviationType.MainComponent, IDeviationStepperFormStep.Step_4),
    },
  ]

  useEffect(() => {
    setDisabled(false)
  }, [setDisabled])

  useEffect(() => {
    if (!getValues('type')) {
      reset({
        ...getValues(),
        type: IDeviationType.Mooring,
      })
    }
  }, [getValues('type')])

  const handleChange = useCallback((e, v, step) => {
    if (getValues('type') !== v) {
      reset({
        ...getValues(),
        type: v,
        mooring: undefined,
        position: undefined,
        component: undefined,
        mainComponent: undefined,
      })
    } else {
      reset({
        ...getValues(),
        type: v,
      })
    }

    if (v === IDeviationType.Locality) {
      setIsStepperHidden(true)
    } else {
      setIsStepperHidden(false)
    }

    setActiveStep(step)
  }, [])

  return (
    <Flex.Row sx={{ px: '8px', gap: '22px', width: '100%' }}>
      {deviationTypes.map(type => (
        <StyledCard onClick={type.onClick} key={type.title}>
          <CardContent>
            <IconArea>
              <type.Icon style={{ maxHeight: 80, maxWidth: 80 }} />
            </IconArea>
            <TitleArea>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                align="center"
                sx={{
                  width: '100%',
                }}
              >
                {type.title}
              </Typography>
            </TitleArea>
          </CardContent>
        </StyledCard>
      ))}
    </Flex.Row>
  )
}

export default SelectTypeStep

const StyledCard = styled(Card)(({ theme }) => ({
  height: '222px',
  width: '222px',
  cursor: 'pointer',
  textAlign: 'center',
  border: `2px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
  borderRadius: '4px',
  padding: 0,
  display: 'flex',
  alignItems: 'stretch',
}))

const CardContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  height: '100%',
  padding: '32px',
  boxSizing: 'border-box',
  width: '100%',
})

const IconArea = styled('div')({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
})

const TitleArea = styled('div')({
  height: '48px',
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  marginTop: 16,
})
